import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const CGV = () => {
  return (
    <div className="min-h-screen bg-white font-['Manrope']" data-testid="cgv-page">
      <SEO
        page="cgv"
        customTitle="Conditions Générales de Vente | HostProtect"
        customDescription="Conditions générales de vente de HostProtect, assurance location courte durée distribuée par PM Hospitality."
      />

      {/* Header */}
      <header className="backdrop-blur-xl bg-white/90 border-b border-neutral-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-sm text-neutral-600 hover:text-[#19294C] transition-colors font-medium"
              data-testid="cgv-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#19294C] to-[#1c2f55] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit']"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.6 }}
          >
            Conditions Générales de Vente
          </motion.h1>
          <motion.p
            className="mt-4 text-white/60 text-sm"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            Dernière mise à jour : Septembre 2026
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <motion.main
        className="max-w-4xl mx-auto px-6 py-12 md:py-16"
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="prose prose-neutral max-w-none prose-headings:font-['Outfit'] prose-headings:text-[#19294C] prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3 prose-p:text-neutral-600 prose-p:leading-relaxed prose-li:text-neutral-600 prose-strong:text-[#19294C]">

          <h2>1. Identification du vendeur / distributeur</h2>
          <p>
            PM Hospitality, SAS immatriculée au RCS d'Épinal sous le numéro 938 069 671
          </p>
          <p>Siège social : 27 rue de la Gare, 88380 Arches (France)</p>
          <p>
            Email : <a href="mailto:contact@hostprotect.io" className="text-[#00B8E4] hover:underline">contact@hostprotect.io</a>
          </p>
          <p>
            PM Hospitality agit en qualité de distributeur d'assurance pour le compte de <strong>MEETCH</strong>, marque commerciale de <strong>PHENOMEN</strong>, SAS au capital de 10 000 €, 141 Avenue de Wagram 75017 Paris, Siren 833 740 699, immatriculée à l'ORIAS sous le n°18 000 514 (<a href="https://www.orias.fr" className="text-[#00B8E4] hover:underline" target="_blank" rel="noopener noreferrer">www.orias.fr</a>), courtier gestionnaire mandaté par l'assureur.
          </p>
          <p>
            L'assureur du programme est <strong>MUTUAIDE ASSISTANCE</strong>, SA au capital de 12 558 240 €, 126 rue de la Piazza – CS 20010 – 93196 Noisy-le-Grand CEDEX, RCS Bobigny 383 974 086, entreprise régie par le Code des assurances et soumise au contrôle de l'Autorité de Contrôle Prudentiel et de Résolution (ACPR), 4 place de Budapest, CS 92459, 75436 Paris Cedex 09.
          </p>

          <h2>2. Objet</h2>
          <p>
            Les présentes Conditions Générales de Vente ont pour objet de définir les modalités de souscription à l'assurance HostProtect, une offre destinée aux propriétaires de logements en location courte durée ainsi qu'aux gestionnaires de biens immobiliers ou conciergeries opérant en leur nom.
          </p>
          <p>
            <strong>La couverture s'applique aux hébergements situés en Europe</strong>, telle que définie dans la notice d'information contractuelle (Allemagne, Andorre, Autriche, Belgique, Bulgarie, Chypre, Croatie, Danemark, Espagne, Estonie, Finlande, France métropolitaine, Gibraltar, Hongrie, Grèce, Irlande, Italie et Îles, Liechtenstein, Lettonie, Lituanie, Luxembourg, Malte, Principauté de Monaco, Norvège, Pays-Bas, Pologne, Portugal, Roumanie, Royaume-Uni, Slovaquie, Slovénie, République Tchèque, Saint-Marin, Suède et Suisse), et conformément aux critères d'éligibilité définis dans cette notice.
          </p>

          <h2>3. Produits et garanties</h2>
          <p>
            HostProtect est une offre d'assurance distribuée par PM Hospitality, agissant pour le compte de MEETCH (PHENOMEN), courtier gestionnaire habilité à distribuer les contrats souscrits auprès de l'assureur MUTUAIDE ASSISTANCE.
          </p>
          <p>Cette offre regroupe les garanties suivantes, telles que définies dans la notice d'information contractuelle n°9484-1 :</p>
          <div className="overflow-x-auto not-prose my-6">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#19294C] text-white text-left">
                  <th className="p-3 font-semibold">Garantie</th>
                  <th className="p-3 font-semibold">Contenu</th>
                  <th className="p-3 font-semibold">Plafond</th>
                </tr>
              </thead>
              <tbody className="text-neutral-600">
                <tr className="border-b border-neutral-200">
                  <td className="p-3 font-semibold text-[#19294C] align-top">Dommages</td>
                  <td className="p-3 align-top">Dommages causés au bien loué et à son mobilier par les locataires et leurs animaux domestiques</td>
                  <td className="p-3 align-top">50 000 € par an (dommages matériels et immatériels consécutifs)</td>
                </tr>
                <tr className="border-b border-neutral-200">
                  <td className="p-3 font-semibold text-[#19294C] align-top">Coûts de nettoyage</td>
                  <td className="p-3 align-top">Coûts de nettoyage inattendus, non prévus dans le nettoyage habituel, rendus nécessaires par un sinistre garanti</td>
                  <td className="p-3 align-top">Inclus dans le plafond annuel de 50 000 €</td>
                </tr>
                <tr className="border-b border-neutral-200">
                  <td className="p-3 font-semibold text-[#19294C] align-top">Perte de revenus</td>
                  <td className="p-3 align-top">Réservations confirmées annulées en raison de dommages causés par le locataire précédent</td>
                  <td className="p-3 align-top">Inclus dans le plafond annuel de 50 000 €, limité à 2 semaines consécutives</td>
                </tr>
                <tr className="border-b border-neutral-200">
                  <td className="p-3 font-semibold text-[#19294C] align-top">Protection œuvre d'art et objets de valeurs</td>
                  <td className="p-3 align-top">Œuvres d'art et objets de valeur endommagés, ainsi que les frais de réparation des vélos mis à disposition</td>
                  <td className="p-3 align-top">1 000 € par an</td>
                </tr>
                <tr>
                  <td className="p-3 font-semibold text-[#19294C] align-top">Nuisibles (option)</td>
                  <td className="p-3 align-top">Frais de désinfection et hébergement provisoire en cas de présence de nuisibles</td>
                  <td className="p-3 align-top">10 000 € max par an et par évènement, dont 1 000 € TTC de frais de désinfection par intervention et par logement et 1 000 € d'hébergement provisoire</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p><strong>Franchise :</strong> 10 % du montant du sinistre avec un minimum de 150 €, pour chaque sinistre et chaque garantie.</p>
          <p className="bg-red-50 border-l-4 border-red-500 p-4 rounded !text-[#19294C]">
            <strong>Limitation du nombre de sinistres :</strong> le nombre de sinistres indemnisables, toutes garanties confondues, est limité à 2 (deux) sinistres par année d'assurance et par bien assuré. Au-delà, les sinistres déclarés pour le bien concerné au titre de la même année d'assurance ne donnent plus lieu à indemnisation.
          </p>
          <p><strong>Délai de carence entre deux sinistres :</strong> 30 jours pour la garantie Perte de revenus et pour la garantie Nuisibles.</p>
          <p>Les exclusions sont précisées dans la notice contractuelle (faute intentionnelle, sous-location, stupéfiants, activités professionnelles autres que la location saisonnière, sinistres sans lien avec une location réservée via la plateforme adhérente, vol et perte des objets de valeur, usure normale, dommages purement esthétiques, frais de détection des nuisibles, etc.).</p>

          <h2>4. Souscription</h2>
          <p>
            La souscription se fait en ligne via <a href="https://www.hostprotect.io" className="text-[#00B8E4] hover:underline" target="_blank" rel="noopener noreferrer">www.HostProtect.io</a> pour l'année civile en cours, jusqu'au 31 décembre. Le contrat est renouvelable automatiquement.
          </p>
          <p>La couverture est ouverte aux particuliers et aux professionnels, pour les hébergements situés en Europe tels que définis à l'article 2, sous réserve des critères de location meublée touristique.</p>
          <p><strong>Délai de carence :</strong> 30 jours à compter de l'adhésion. Pendant cette période, aucune demande de remboursement ne peut être formulée.</p>
          <p><strong>Déclaration de sinistre :</strong> tout sinistre doit être déclaré dès sa connaissance et au plus tard dans les 15 jours (pour la garantie Dommages, dans les 15 jours suivant le départ du locataire), sur <a href="https://claim.meetch.io/cover" className="text-[#00B8E4] hover:underline" target="_blank" rel="noopener noreferrer">https://claim.meetch.io/cover</a>.</p>
          <p><strong>Renonciation au droit de rétractation :</strong> En payant en ligne via Stripe, l'exécution immédiate du contrat est demandée, annulant le droit de rétractation.</p>

          <h2>5. Tarifs et paiement</h2>
          <p>Le client peut choisir :</p>
          <ul>
            <li><strong>Paiement en une fois :</strong> 189 € HT par logement pour l'année civile (hors promotions).</li>
          </ul>
          <p>Le paiement est sécurisé via Stripe. En cas d'échec, un rappel est envoyé, et le client dispose de 10 jours pour régulariser avant suspension du contrat.</p>

          <h2>6. Durée et renouvellement</h2>
          <p>
            La couverture est valable jusqu'au 31 décembre de l'année de souscription et est renouvelable automatiquement chaque 1er janvier, sauf résiliation.
          </p>

          <h2>7. Droit de rétractation</h2>
          <p>
            Conformément au Code des assurances et au Code de la consommation, le souscripteur dispose initialement d'un délai de 14 jours pour se rétracter. Cependant, la demande d'exécution immédiate via Stripe entraîne la renonciation à ce droit pour bénéficier de la couverture immédiatement.
          </p>

          <h2>8. Responsabilité du distributeur</h2>
          <p>
            PM Hospitality agit uniquement comme distributeur d'assurance et n'est pas responsable de la conception ni de la gestion des contrats. Cette responsabilité incombe à MEETCH (PHENOMEN), courtier gestionnaire, et à MUTUAIDE ASSISTANCE, assureur.
          </p>
          <p>Le distributeur n'est pas responsable :</p>
          <ul>
            <li>Des refus ou limitations d'indemnisation ;</li>
            <li>Des délais de traitement ;</li>
            <li>De la gestion des réclamations.</li>
          </ul>

          <h2>9. Résiliation</h2>
          <p>Le contrat peut être résilié :</p>
          <ul>
            <li>Par le client à l'échéance annuelle avec un préavis d'un mois ;</li>
            <li>En cas de résiliation du contrat de distribution ;</li>
            <li>En cas de résiliation du contrat d'assurance par l'assureur ou le souscripteur, et dans l'hypothèse où la garantie ne serait pas transférée à un nouvel assureur, le client en étant informé au moins deux mois avant la date d'effet ;</li>
            <li>En cas de non-respect des conditions contractuelles ;</li>
            <li>Dans les cas prévus par le Code des assurances.</li>
          </ul>

          <h2>10. Données personnelles</h2>
          <p>
            Les données personnelles sont traitées par PM Hospitality, MEETCH (PHENOMEN) et MUTUAIDE ASSISTANCE conformément au RGPD, et utilisées uniquement pour :
          </p>
          <ul>
            <li>La souscription et la gestion du contrat ;</li>
            <li>Le traitement des réclamations ;</li>
            <li>L'accès à l'espace client ;</li>
            <li>Le respect des obligations légales.</li>
          </ul>
          <p>
            Pour exercer vos droits RGPD auprès du distributeur : <a href="mailto:contact@hostprotect.io" className="text-[#00B8E4] hover:underline">contact@hostprotect.io</a>. Auprès de l'assureur : Délégué représentant à la protection des données – MUTUAIDE ASSISTANCE – 126, rue de la Piazza – CS 20010 – 93196 Noisy-le-Grand CEDEX, ou <a href="mailto:DRPO@MUTUAIDE.fr" className="text-[#00B8E4] hover:underline">DRPO@MUTUAIDE.fr</a>.
          </p>
          <p>
            <strong>Réclamations :</strong> par e-mail à <a href="mailto:reclamation@meetch.io" className="text-[#00B8E4] hover:underline">reclamation@meetch.io</a> ou par courrier à MEETCH – 141 Avenue de Wagram 75017 Paris. En cas de litige persistant : <a href="https://www.mediation-assurance.org" className="text-[#00B8E4] hover:underline" target="_blank" rel="noopener noreferrer">www.mediation-assurance.org</a> ou Médiation de l'Assurance, TSA 50110, 75441 Paris Cedex 09.
          </p>

          <h2>11. Loi applicable</h2>
          <p>Les CGV sont soumises au droit français. En cas de litige :</p>
          <ul>
            <li>Le tribunal compétent est celui du domicile du consommateur pour les clients particuliers ;</li>
            <li>Pour les professionnels, tribunal du siège de PM Hospitality sauf disposition légale contraire.</li>
          </ul>

        </div>
      </motion.main>

      {/* Footer */}
      <footer className="bg-[#19294C] text-white py-12 px-6 md:px-12" data-testid="cgv-footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <Link to="/" className="flex items-center">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto brightness-0 invert" />
            </Link>
            <div className="flex items-center gap-6 text-sm text-white/60">
              <Link to="/politique-de-confidentialite" className="hover:text-white transition-colors" data-testid="cgv-link-privacy">
                Politique de confidentialité
              </Link>
              <Link to="/connexion" className="hover:text-white transition-colors">
                Espace client
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="text-white/40 text-sm">© {new Date().getFullYear()} HostProtect — PM Hospitality SAS. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CGV;
